import { useCallback, useEffect, useState } from 'react';
import { Confirm, toast, type Destructive } from '@drbell/ui';
import {
  api, ApiError, MANAGERS,
  type OrgDetail, type OrgSummary, type Resident, type RotaAssignment,
  type RotaNow, type Shift,
} from '../api.ts';
import { localDateAt } from '@drbell/core';

/**
 * The rota - RX-ORG-14.
 *
 * Two questions on one screen, because they are asked minutes apart: who is
 * on today, and what is still outstanding for them right now.
 *
 * Assignment is not access. Ward scoping decides what a nurse may SEE; this
 * decides what she is responsible for today, and the two differ constantly.
 * Nothing on this screen grants or removes access to a record.
 */
/**
 * Today on the home's calendar. This was UTC's date, so in India the rota
 * opened on yesterday until 05:30 — at the night shift's busiest hour.
 */
const today = (timeZone: string) => localDateAt(Date.now(), timeZone);

export function Rota({
  org, detail, residents, onChanged,
}: {
  org: OrgSummary;
  detail: OrgDetail;
  residents: Resident[];
  onChanged: () => void;
}) {
  const [date, setDate] = useState(today(org.timezone || "Asia/Kolkata"));
  const [shifts, setShifts] = useState<Shift[] | null>(null);
  const [assignments, setAssignments] = useState<RotaAssignment[] | null>(null);
  const [live, setLive] = useState<RotaNow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ shiftId: string } | null>(null);
  const [addingShift, setAddingShift] = useState(false);
  const [pending, setPending] = useState<Destructive | null>(null);

  const canManage = MANAGERS.includes(detail.yourRole);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, r, n] = await Promise.all([
        api.get<{ shifts: Shift[] }>(`/api/orgs/${org.id}/shifts`),
        api.get<{ assignments: RotaAssignment[] }>(`/api/orgs/${org.id}/rota?date=${date}`),
        api.get<RotaNow>(`/api/orgs/${org.id}/rota/now`),
      ]);
      setShifts(s.shifts);
      setAssignments(r.assignments);
      setLive(n);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [org.id, date]);

  useEffect(() => { void load(); }, [load]);

  const addShift = async (v: { name: string; startTime: string; endTime: string; crossesMidnight: boolean }) => {
    await api.post(`/api/orgs/${org.id}/shifts`, {
      ...v,
      sortOrder: (shifts?.length ?? 0) + 1,
    });
    await load();
    setAddingShift(false);
    toast.success(`${v.name} added`, `${v.startTime}–${v.endTime}${v.crossesMidnight ? ', into the next day' : ''}`);
  };

  if (error) {
    return <p className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-500">{error}</p>;
  }
  if (!shifts || !assignments) return <p className="text-(--muted)">Loading…</p>;

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm">
          <span className="mr-2 text-(--muted)">Day</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value || today(org.timezone || "Asia/Kolkata"))}
            className="min-h-11 rounded-lg border border-(--line) bg-(--bg) px-3"
          />
        </label>
        {date !== today(org.timezone || "Asia/Kolkata") && (
          <button
            onClick={() => setDate(today(org.timezone || "Asia/Kolkata"))}
            className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            Back to today
          </button>
        )}
        {canManage && (
          <button
            onClick={() => setAddingShift(true)}
            className="ml-auto min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            + Add a shift
          </button>
        )}
      </div>

      {shifts.length === 0 && (
        <p className="rounded-xl border border-dashed border-(--line) p-6 text-center text-sm text-(--muted)">
          No shifts defined yet. Add the ones this home actually runs — the
          rota is built out of them.
        </p>
      )}

      {/*
        Who is on right now, and what is outstanding for them — only on
        today's page. It is about this minute, not the day being looked at,
        and it uses the rule the alerts use: a shift counts while it is
        running, including a night shift that began yesterday.
      */}
      {live && date === today(org.timezone || "Asia/Kolkata") && (
        live.nobodyOn ? (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
            <p className="font-medium">Nobody is on shift right now.</p>
            <p className="text-(--muted)">
              Until somebody is, a late or missed dose is sent to every member of staff who can see
              that resident.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-(--line) bg-(--panel) p-5">
            <h2 className="mb-3 text-lg font-semibold">On shift right now</h2>
            <div className="space-y-4">
              {live.staff.map((s) => (
                <div key={s.id}>
                  <p className="text-sm font-medium">
                    {s.staffName ?? s.staffPhoneHint ?? 'Unnamed'}{' '}
                    <span className="text-(--muted)">
                      · {s.shiftName}, until {clock(s.endsAt, live.timeZone)} · {s.residents} resident{s.residents === 1 ? '' : 's'}
                    </span>
                  </p>
                  {s.outstanding.length === 0 ? (
                    <p className="mt-1 text-sm text-(--muted)">
                      {s.residents === 0 ? 'No residents ticked for this shift — alerts for residents go to everyone.' : 'Nothing outstanding.'}
                    </p>
                  ) : (
                    <ul className="mt-1 space-y-1">
                      {s.outstanding.map((d) => (
                        <li key={d.id} className="flex flex-wrap items-baseline gap-2 text-sm">
                          <span className="tabular-nums text-(--muted)">{clock(d.dueAt, live.timeZone)}</span>
                          <span className="font-medium">{d.displayName}</span>
                          {d.locationNote && <span className="text-xs text-(--muted)">{d.locationNote}</span>}
                          <span>{d.medicationName}</span>
                          {d.isCritical && (
                            <span className="rounded-full border border-red-500/40 px-2 text-xs text-red-500">critical</span>
                          )}
                          {d.overdue && <span className="text-xs font-medium text-red-500">window closed</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {shifts.map((shift) => {
        const on = assignments.filter((a) => a.shiftId === shift.id);
        return (
          <div key={shift.id} className="rounded-xl border border-(--line) p-5">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-semibold">{shift.name}</h2>
              <span className="text-sm text-(--muted)">
                {shift.startTime}–{shift.endTime}
                {shift.crossesMidnight && ' (next day)'}
              </span>
              {canManage && (
                <button
                  onClick={() => setEditing({ shiftId: shift.id })}
                  className="ml-auto min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
                >
                  Put somebody on
                </button>
              )}
              {/*
                Retiring a shift — RX-ORG-14.

                A shift could be created and never removed, so a home that
                changed from three shifts to two carried a dead one on its rota
                for ever. Archived, not deleted: past days still show who was
                on it.
              */}
              {canManage && (
                <button
                  onClick={() =>
                    setPending({
                      // Quoted, not "the X shift": a shift is often NAMED "Night shift".
                      title: `Retire “${shift.name}”?`,
                      body:
                        on.length > 0
                          ? `It stops appearing on the rota from now on. ${on.length} ${on.length === 1 ? 'person is' : 'people are'} on it for this day — put them on another shift first if they are still working.`
                          : 'It stops appearing on the rota from now on.',
                      reassurance: 'Past days keep it, so the rota can still say who was on that shift.',
                      confirmLabel: 'Retire shift',
                      onConfirm: async () => {
                        await api.del(`/api/orgs/${org.id}/shifts/${shift.id}`);
                        toast.success(`${shift.name} retired`);
                        setPending(null);
                        await load();
                      },
                    })
                  }
                  className="min-h-11 rounded-lg border border-(--line) px-3 text-sm text-(--muted) hover:border-red-500 hover:text-red-600"
                >
                  Retire
                </button>
              )}
            </div>

            {on.length === 0 ? (
              <p className="mt-3 text-sm text-(--muted)">Nobody on this shift.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {on.map((a) => (
                  <li key={a.id} className="rounded-lg border border-(--line) bg-(--panel) p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {a.staffName ?? a.staffPhoneHint ?? 'Unnamed account'}
                      </span>
                      {a.note && <span className="text-sm text-(--muted)">· {a.note}</span>}
                      {canManage && (
                        <button
                          onClick={() =>
                            setPending({
                              title: `Take ${a.staffName ?? 'this person'} off ${shift.name} on ${date}?`,
                              body:
                                a.residents.length === 0
                                  ? 'Nobody is currently assigned to them on this shift, so nothing else changes.'
                                  : `The ${a.residents.length} resident${a.residents.length === 1 ? '' : 's'} they were responsible for on this shift become nobody's until somebody else is put on.`,
                              reassurance:
                                'Their access to records is unchanged. The rota says who is responsible today, not who may see what.',
                              confirmLabel: 'Take them off',
                              onConfirm: async () => {
                                await api.del(`/api/orgs/${org.id}/rota/${a.id}`);
                                toast.success(
                                  `${a.staffName ?? 'They'} came off ${shift.name}`,
                                  'Their access to records is unchanged.',
                                );
                                await load();
                                onChanged();
                              },
                            })
                          }
                          className="ml-auto min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-red-500"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-(--muted)">
                      {a.residents.length === 0
                        ? 'No residents assigned'
                        : a.residents.map((r) => `${r.displayName}${r.locationNote ? ` (${r.locationNote})` : ''}`).join(' · ')}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      {addingShift && (
        <ShiftDialog
          existingNames={shifts.map((s) => s.name)}
          onClose={() => setAddingShift(false)}
          onSave={addShift}
        />
      )}

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}

      {editing && (
        <AssignDialog
          orgId={org.id}
          shiftId={editing.shiftId}
          date={date}
          members={detail.members.filter((m) => m.status === 'active' && m.acceptedAt !== null)}
          residents={residents}
          existing={assignments.filter((a) => a.shiftId === editing.shiftId)}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </section>
  );
}

function AssignDialog({
  orgId, shiftId, date, members, residents, existing, onClose, onSaved,
}: {
  orgId: string;
  shiftId: string;
  date: string;
  members: OrgDetail['members'];
  residents: Resident[];
  existing: RotaAssignment[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [userId, setUserId] = useState(members[0]?.userId ?? '');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill from an existing assignment, so editing is editing rather than
  // silently replacing.
  useEffect(() => {
    const current = existing.find((a) => a.userId === userId);
    setPicked(new Set(current?.residents.map((r) => r.id) ?? []));
    setNote(current?.note ?? '');
  }, [userId, existing]);

  /* Somebody already on this shift, so the list can say so. */
  const alreadyOn = new Set(existing.map((a) => a.userId));

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="assign-shift-title">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 id="assign-shift-title" className="text-lg font-semibold">Put somebody on this shift</h2>
        <p className="mt-1 text-sm text-(--muted)">{date}</p>

        {members.length === 0 ? (
          <p className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
            {/* The commonest reason this dialog is empty, said rather than
                left as a blank dropdown. */}
            Nobody has accepted an invitation yet. An invited nurse appears
            here only once she has signed in and accepted — until then she has
            no access and cannot be given a round.
          </p>
        ) : (
          <>
            <label className="mt-4 block">
              <span className="text-xs text-(--muted)">Who</span>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3 text-sm"
              >
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name ?? m.phone ?? m.userId}
                    {alreadyOn.has(m.userId) ? ' — already on' : ''}
                  </option>
                ))}
              </select>
            </label>

            <fieldset className="mt-4">
              <legend className="text-xs text-(--muted)">
                Residents they are responsible for ({picked.size} of {residents.length})
              </legend>
              <div className="mt-1 max-h-56 space-y-1 overflow-y-auto rounded-lg border border-(--line) p-2">
                {residents.length === 0 && (
                  <p className="p-2 text-sm text-(--muted)">No residents yet.</p>
                )}
                {residents.map((r) => (
                  <label key={r.id} className="flex min-h-10 cursor-pointer items-center gap-2 px-1 text-sm">
                    <input
                      type="checkbox"
                      checked={picked.has(r.id)}
                      onChange={(e) => {
                        const next = new Set(picked);
                        if (e.target.checked) next.add(r.id);
                        else next.delete(r.id);
                        setPicked(next);
                      }}
                    />
                    {r.displayName}
                    {r.locationNote && <span className="text-xs text-(--muted)">{r.locationNote}</span>}
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="mt-4 block">
              <span className="text-xs text-(--muted)">Note — optional</span>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Covering ward 1"
                className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3 text-sm"
              />
            </label>
          </>
        )}

        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
            Cancel
          </button>
          <button
            disabled={busy || !userId}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const result = await api.post<{ rejected?: string[] }>(`/api/orgs/${orgId}/rota`, {
                  shiftId, userId, localDate: date, note: note || undefined,
                  patientIds: [...picked],
                });
                // The server names what it refused; showing a success that
                // quietly dropped somebody would be worse than an error.
                if (result.rejected?.length) {
                  setError(`${result.rejected.length} resident(s) were not saved — they do not belong to this organisation.`);
                  setBusy(false);
                  return;
                }
                await onSaved();
              } catch (e) {
                setError(e instanceof ApiError ? e.message : String(e));
                setBusy(false);
              }
            }}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Defining a shift, as one form.
 *
 * It was three chained browser popups: name, then times, then a yes/no about
 * midnight. Chrome suppresses every popup after the first if the user ticks
 * the box, and Safari can block them outright — so the second prompt returned
 * null, the function bailed, and a manager who had just typed "Nights" saw
 * absolutely nothing happen. Three popups is also three chances to be blocked
 * for one record.
 *
 * `crossesMidnight` is still asked rather than inferred, for the reason it
 * always was: `end < start` cannot distinguish a real night shift from a
 * mis-typed day one, and guessing wrong puts the shift on the wrong date.
 */
function ShiftDialog({ existingNames, onClose, onSave }: {
  existingNames: string[];
  onClose: () => void;
  onSave: (v: { name: string; startTime: string; endTime: string; crossesMidnight: boolean }) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('15:00');
  const [crossesMidnight, setCrossesMidnight] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clash = existingNames.some((n) => n.toLowerCase() === name.trim().toLowerCase());
  const wraps = endTime <= startTime;
  const valid = name.trim().length >= 2 && !clash && /^\d{2}:\d{2}$/.test(startTime) && /^\d{2}:\d{2}$/.test(endTime);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="new-shift-title">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 id="new-shift-title" className="text-lg font-semibold">New shift</h2>
        <p className="mt-1 text-sm text-(--muted)">
          Whatever this home actually calls it. The rota is built out of these.
        </p>

        <label className="mt-4 block">
          <span className="text-xs text-(--muted)">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Early, Late, Nights…"
            autoFocus
            className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
          />
          {clash && <span className="mt-1 block text-xs text-amber-600">There is already a shift called that.</span>}
        </label>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-(--muted)">Starts</span>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
            />
          </label>
          <label className="block">
            <span className="text-xs text-(--muted)">Ends</span>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
            />
          </label>
        </div>

        {wraps && (
          <label className="mt-3 flex min-h-11 items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
            <input
              type="checkbox"
              checked={crossesMidnight}
              onChange={(e) => setCrossesMidnight(e.target.checked)}
              className="mt-1"
            />
            <span>
              This shift runs past midnight into the next day.
              <span className="mt-1 block text-xs text-(--muted)">
                Leave it unticked if the times are simply the wrong way round —
                ticking it wrongly puts the shift on the wrong date.
              </span>
            </span>
          </label>
        )}

        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
            Cancel
          </button>
          <button
            disabled={!valid || busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onSave({
                  name: name.trim(),
                  startTime,
                  endTime,
                  // Only meaningful when the times wrap; a day shift can never
                  // cross midnight however the box was left.
                  crossesMidnight: wraps && crossesMidnight,
                });
              } catch (e) {
                setError(e instanceof ApiError ? e.message : String(e));
                setBusy(false);
              }
            }}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Add shift'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** A time on the home's own clock, not the browser's. */
function clock(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', timeZone }).format(ms);
}

